import { emailApiService } from './emailApiService';
import { parseOrderEmails, extractOriginalOrderSection } from '../utils/emailParser';
import {
  processEmailOrder,
  findOrPrepareClient,
  createClientFromParsedData,
  createOrderFromParsedData
} from '../utils/emailOrderProcessor';
import { Order } from '../types/order';
import { Client } from '../types/client';
import { Dispatch } from '@reduxjs/toolkit';
import { addOrder } from '../store/slices/ordersSlice';
import { addClient } from '../store/slices/clientsSlice';
import { addReviewItem } from '../store/slices/reviewQueueSlice';

// Helper function to get display name for any client type
const getClientDisplayName = (client: Client): string => {
  return client.type === 'individual' ? client.name : client.retailerName;
};

export interface EmailCheckResult {
  success: boolean;
  totalEmails: number;
  ordersCreated: number;
  ordersFlaggedForReview: number;
  errorMessage?: string;
}

/**
 * Main workflow function that orchestrates the entire email checking process:
 * 1. Fetch emails from IMAP
 * 2. Parse emails
 * 3. Process each email (validate & check for duplicates)
 * 4. Create orders or add to review queue
 * 5. Return summary results
 */
export const runEmailCheckWorkflow = async (
  dispatch: Dispatch,
  existingOrders: Order[],
  existingClients: Client[],
  availableRecipes: { id: string; name: string }[]
): Promise<EmailCheckResult> => {
  try {
    console.log('📧 Starting email check workflow...');

    // Step 1: Fetch emails via API
    const emails = await emailApiService.fetchOrderEmails();
    console.log(`📬 Fetched ${emails.length} emails`);

    if (emails.length === 0) {
      return {
        success: true,
        totalEmails: 0,
        ordersCreated: 0,
        ordersFlaggedForReview: 0
      };
    }

    // Step 2: Parse emails (with product filtering)
    const parsedEmails = parseOrderEmails(emails, availableRecipes);
    console.log(`📄 Parsed ${parsedEmails.length} emails`);

    // Step 3: Process each email
    let ordersCreated = 0;
    let ordersFlaggedForReview = 0;

    for (const parsedEmail of parsedEmails) {
      const result = processEmailOrder(parsedEmail, existingOrders, availableRecipes);

      if (result.action === 'auto-create') {
        // Create order automatically
        if (result.parsedData) {
          // Find or create client
          const clientResult = findOrPrepareClient(result.parsedData.email, result.parsedData.phone, existingClients);

          // Create client if new
          if (!clientResult.isExisting) {
            const newClient = createClientFromParsedData(result.parsedData, clientResult.clientId);
            dispatch(addClient(newClient));
            console.log(`✅ Created new client: ${getClientDisplayName(newClient)}`);
          } else {
            console.log(
              `🔗 Linked to existing client: ${clientResult.existingClient ? getClientDisplayName(clientResult.existingClient) : 'unknown'}`
            );
          }

          // Extract original order text from email
          const originalOrderText = extractOriginalOrderSection(parsedEmail.rawEmail.body);

          // Create order
          const newOrder = createOrderFromParsedData(
            result.parsedData,
            clientResult.clientId,
            availableRecipes,
            originalOrderText
          );
          dispatch(addOrder(newOrder));
          ordersCreated++;
          console.log(`✅ Created order for: ${newOrder.customerName}`);
        }
      } else if (result.action === 'review-queue') {
        // Add to review queue
        if (result.reviewQueueItem) {
          dispatch(addReviewItem(result.reviewQueueItem));
          ordersFlaggedForReview++;
          console.log(
            `⚠️  Flagged for review: ${result.flagReason} - ${result.reviewQueueItem.parsedData.customerName}`
          );
        }
      }
    }

    console.log('✅ Email check workflow completed successfully');
    return {
      success: true,
      totalEmails: emails.length,
      ordersCreated,
      ordersFlaggedForReview
    };
  } catch (error) {
    console.error('❌ Email check workflow failed:', error);
    return {
      success: false,
      totalEmails: 0,
      ordersCreated: 0,
      ordersFlaggedForReview: 0,
      errorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};
